import { motion } from "framer-motion";
import { MapPin, Sparkles, Bike, Wrench, History } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const SCREENS = [
  { src: "https://customer-assets.emergentagent.com/job_rider-collective/artifacts/4n74da8i_photo_6034871475921161826_y.jpg", labelKey: "liveMap", icon: MapPin, rotate: "-rotate-[8deg]", z: "z-10", float: "float-y" },
  { src: "https://customer-assets.emergentagent.com/job_rider-collective/artifacts/q9xr9vez_photo_6034871475921161821_y.jpg", labelKey: "aiRoutes", icon: Sparkles, rotate: "rotate-0", z: "z-20", float: "float-y-slow" },
  { src: "https://customer-assets.emergentagent.com/job_rider-collective/artifacts/3lk16hp7_photo_6034871475921161822_y.jpg", labelKey: "garage", icon: Bike, rotate: "rotate-[8deg]", z: "z-10", float: "float-y" },
];
const SECONDARY = [
  { src: "https://customer-assets.emergentagent.com/job_rider-collective/artifacts/07rdonkx_photo_6034871475921161824_y.jpg", labelKey: "maintenance", icon: Wrench },
  { src: "https://customer-assets.emergentagent.com/job_rider-collective/artifacts/qzsnjk74_photo_6034871475921161823_y.jpg", labelKey: "history", icon: History },
];

const PhoneFrame = ({ screen, label, className = "", testId, size = "lg" }) => {
  const Icon = screen.icon;
  const widths = { lg: "w-[230px] sm:w-[260px]", md: "w-[180px] sm:w-[210px]" };
  return (
    <div data-testid={testId} className={`relative ${className}`}>
      <div className="absolute -inset-6 rounded-[60px] bg-[#39FF14]/10 blur-3xl pointer-events-none" />
      <div className={`relative ${widths[size]} aspect-[9/19.5] rounded-[40px] border border-white/14 bg-black/80 backdrop-blur-xl p-[5px] shadow-[0_30px_90px_rgba(0,0,0,0.65)]`}>
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-10" />
        <div className="relative w-full h-full rounded-[35px] overflow-hidden bg-[#0B0F12]">
          <img src={screen.src} alt={label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </div>
      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-black/70 backdrop-blur-xl px-3 py-1.5 shadow-[0_18px_40px_rgba(0,0,0,0.45)]">
        <Icon className="w-3 h-3 text-[#39FF14]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/85">{label}</span>
      </div>
    </div>
  );
};

export const AppPreview = () => {
  const { t } = useLanguage();
  const highlights = [
    { icon: MapPin, ...t.appPreview.hl1 },
    { icon: Sparkles, ...t.appPreview.hl2 },
    { icon: Wrench, ...t.appPreview.hl3 },
  ];
  return (
    <section id="app-preview" data-testid="app-preview-section" className="relative py-20 sm:py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 hero-backdrop opacity-70 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 14, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#39FF14]/90">{t.appPreview.eyebrow}</span>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight">
            {t.appPreview.title1}<br />{t.appPreview.title2}
          </h2>
          <p className="mt-4 text-white/65 text-base sm:text-lg leading-relaxed">{t.appPreview.subhead}</p>
        </motion.div>

        <div className="relative mt-20 sm:mt-24 min-h-[560px] sm:min-h-[640px] flex items-center justify-center">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-[#39FF14]/8 blur-3xl pointer-events-none" />
          <div className="relative flex items-center justify-center">
            <motion.div
              initial={{ y: 28, x: -20, opacity: 0 }} whileInView={{ y: 0, x: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease, delay: 0.05 }}
              className={`hidden md:block ${SCREENS[0].z} -mr-20 lg:-mr-24`}
            >
              <PhoneFrame screen={SCREENS[0]} label={t.appPreview.label[SCREENS[0].labelKey]} testId="app-mockup-live-map" className={`${SCREENS[0].rotate} ${SCREENS[0].float}`} size="lg" />
            </motion.div>
            <motion.div
              initial={{ y: 28, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease, delay: 0.18 }}
              className={`relative ${SCREENS[1].z}`}
            >
              <PhoneFrame screen={SCREENS[1]} label={t.appPreview.label[SCREENS[1].labelKey]} testId="app-mockup-routes" className={`${SCREENS[1].rotate} ${SCREENS[1].float}`} size="lg" />
            </motion.div>
            <motion.div
              initial={{ y: 28, x: 20, opacity: 0 }} whileInView={{ y: 0, x: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease, delay: 0.3 }}
              className={`hidden md:block ${SCREENS[2].z} -ml-20 lg:-ml-24`}
            >
              <PhoneFrame screen={SCREENS[2]} label={t.appPreview.label[SCREENS[2].labelKey]} testId="app-mockup-garage" className={`${SCREENS[2].rotate} ${SCREENS[2].float}`} size="lg" />
            </motion.div>
          </div>
        </div>

        <div className="mt-10 sm:mt-14 grid grid-cols-2 gap-6 sm:gap-10 max-w-2xl mx-auto justify-items-center">
          {SECONDARY.map((s, i) => (
            <motion.div
              key={s.labelKey}
              initial={{ y: 24, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease, delay: i * 0.1 }}
              className="relative"
            >
              <PhoneFrame screen={s} label={t.appPreview.label[s.labelKey]} testId={`app-mockup-secondary-${i}`} size="md" />
            </motion.div>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {highlights.map((h) => {
            const Icon = h.icon;
            return (
              <div key={h.title} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14]">
                  <Icon className="w-4 h-4" />
                </span>
                <div>
                  <div className="font-display font-semibold text-white text-sm">{h.title}</div>
                  <div className="text-xs text-white/60 mt-0.5">{h.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
